<script setup lang="ts">
import { i18n } from '@/i18n';
import EditPageLayout from './EditPageLayout.vue';
import { computed, nextTick, onMounted, ref } from 'vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { MODE_CONFIG } from '../constants';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { EditBusinessViewModel } from './editBusinessViewModel';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { HeaderList } from '@/coreDomainsModule/table';
import TableCheckbox from '../components/TableCheckbox.vue';
import SegmentControl from '@/corePagesModule/components/SegmentControl.vue';
import { EditBusinessMode } from './useEditBusinessStore/types';
import { useBulkEditBusinessStore } from './useEditBusinessStore';
import { storeToRefs } from 'pinia';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';
import UpdateBaseBusinessWideSection from '../components/UpdateBaseBusinessWideSection.vue';
import UpdateAggregateBusinessWideSection from '../components/UpdateAggregateBusinessWideSection.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { ErrorResponseCode } from '@/coreInfrastructureModule/axios/generated/aggregateBusinessManage/api';
import { diContainer } from '@/coreContainer/container';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';

// #region Component constants
const SLIDE_DOWN_HEIGHT = 280;
const SLIDE_DOWN_TABLE_GAP = 8;
const SLIDE_DOWN_ANIMATION_DURATION_MS = 300;
const TABLE_RESIZE_ANIMATION_DURATION_MS = 120;
const TABLE_VIEWPORT_BOTTOM_MARGIN = 16;
const SLIDE_DOWN_Z = 900;
const t = i18n.t;
const vm = new EditBusinessViewModel();
const store = useBulkEditBusinessStore();
const { state } = storeToRefs(store);
const { init, fetchEditBusiness, additionalValidate } = store;
// #endregion

/**
 * テーブル追従アニメーションで使用するCSS値
 */
type TableSlideAnimationState = {
  fixedTop: string;
  fixedLeft: string;
  fixedRight: string;
  fixedHeight: string;
  finalHeight: string;
  pushDistance: string;
  pushDelay: string;
  pushDuration: string;
  resizeDelay: string;
};

const DEFAULT_TABLE_SLIDE_ANIMATION_STATE: TableSlideAnimationState = {
  fixedTop: '0px',
  fixedLeft: '0px',
  fixedRight: '0px',
  fixedHeight: '0px',
  finalHeight: '0px',
  pushDistance: '0px',
  pushDelay: '0s',
  pushDuration: `${SLIDE_DOWN_ANIMATION_DURATION_MS}ms`,
  resizeDelay: `${SLIDE_DOWN_ANIMATION_DURATION_MS}ms`,
};

/**
 * 数値をpx単位のCSS値へ変換する
 * @param value - 変換する数値
 */
const toPx = (value: number) => `${value}px`;

/**
 * 数値をms単位のCSS値へ変換する
 * @param value - 変換する数値
 */
const toMs = (value: number) => `${value}ms`;

// #region Refs
const updateBaseBusinessWideSectionRef = ref<InstanceType<typeof UpdateBaseBusinessWideSection> | null>(null);
const updateAggregateBusinessWideSectionRef = ref<InstanceType<typeof UpdateAggregateBusinessWideSection> | null>(null);
const tableContainerRef = ref<HTMLElement | null>(null);
const tableRef = ref<HTMLElement | null>(null);
const tableSlideAnimationState = ref<TableSlideAnimationState>(DEFAULT_TABLE_SLIDE_ANIMATION_STATE);
const tableWidthInfo = ref<Record<string, number | undefined>>({});
// #endregion

// #region Computed refs
const pageTitle = computed(() => `${t('取引一括編集')}`);
const modeConfig = computed(() => MODE_CONFIG[state.value.businessMode]);
const tableType = computed(() => modeConfig.value.tableType.bulk);
const businessFreeColumnHeaderOptions = computed(() => {
  const headers = new Map<number, string>();
  for (const business of state.value.dataList) {
    for (const column of business.businessFreeColumnValues ?? []) {
      if (column.isConfigured) headers.set(column.columnNo, column.headerName);
    }
  }
  return [...headers.entries()]
    .sort(([a], [b]) => a - b)
    .map(([columnNo, headerName]) => ({ key: `businessFreeColumnValues-${columnNo}`, label: headerName, type: 'str' as const }));
});
const fullHeaderOptions = computed(() => {
  const trailingHeaderKeys = ['createdAt', 'updatedAt', 'headerModify'];
  const trailingHeaderOptions = trailingHeaderKeys
    .map((key) => modeConfig.value.headerOptions.find((h) => h.key === key))
    .filter((h): h is NonNullable<typeof h> => h !== undefined);

  return [
    ...modeConfig.value.headerOptions.filter((h) => !trailingHeaderKeys.includes(h.key)),
    ...businessFreeColumnHeaderOptions.value,
    ...trailingHeaderOptions,
  ];
});
const fullHeaderKeys = computed(() => fullHeaderOptions.value.map((h) => h.key));

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({ tableType: tableType, widthInfo: tableWidthInfo });
const { onMousedownResizer } = useColumnResizer({
  tableType: tableType,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const headerOptions = computed(() => fullHeaderOptions.value.filter((h) => headerList.value.includes(h.key)));
const headerWidths = computed(() => headerOptions.value.map((h) => `${vm.getHeaderWidth(h, tableWidthInfo.value)}px`));
const ModifiableHeaderKeys = computed(
  (): HeaderList =>
    fullHeaderOptions.value
      .filter((h) => !['checkbox', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: h.label,
        isShown: headerList.value.includes(h.key),
      }))
);
const dataListBusinessIds = computed(() => state.value.dataList.map((d) => d.businessId));
const checkedCount = computed(() => state.value.checkedIds.size);
const isAllChecked = computed(() => !!state.value.dataList.length && checkedCount.value === state.value.dataList.length);
const isIndeterminate = computed(() => !!checkedCount.value && !isAllChecked.value);
const updateSectionRefWithMode = computed(() =>
  state.value.businessMode === 'baseBusiness' ? updateBaseBusinessWideSectionRef.value : updateAggregateBusinessWideSectionRef.value
);
// #endregion

// #region Functions
/**
 * 取引タイプを切り替えた際の処理
 * @param businessMode - 取引タイプ
 */
const changeBusinessMode = (businessMode: EditBusinessMode) => {
  const pageSize = state.value.pagingOptions.pageSize;
  init(async () => {
    state.value.businessMode = businessMode;
    state.value.pagingOptions = initPagingOptions(pageSize);
  });
};

/**
 * ヘッダーのチェックボックスを押した際のハンドラ
 * @param isChecked - チェックボックスの状態
 */
const changeHeaderCheckbox = (isChecked: boolean) => {
  if (isChecked) {
    const next = new Set(dataListBusinessIds.value);
    state.value.checkedIds = next;
  } else {
    state.value.checkedIds = new Set();
  }
};

/**
 * 行のチェックボックスを押した際のハンドラ
 * @param businessId - 当該行取引ID
 * @param isChecked - チェックボックスの状態
 */
const changeCheckbox = (businessId: string, isChecked: boolean) => {
  const next = new Set(state.value.checkedIds);
  if (isChecked) {
    next.add(businessId);
  } else {
    next.delete(businessId);
  }
  state.value.checkedIds = next;
};

/**
 * フィルターを適用した際の処理
 * @param filterItems - フィルターアイテム
 */
const applyFilter = async (filterItems: FilterContentItem[]) => {
  state.value.filterItems = filterItems;
  await fetchEditBusiness();
};

/**
 * ページング変更時の処理
 * @param pagingOptions - ページングオプション
 */
const changePaging = async (pagingOptions: IPagingOptions) => {
  state.value.pagingOptions = pagingOptions;
  await fetchEditBusiness();
};

/**
 * スライドダウンがテーブルに到達したタイミングで押し下げるためのアニメーション値を作成する
 * @param tableRect - ドロップダウンを開く直前のテーブル表示領域
 */
const createTableSlideAnimationState = (tableRect: DOMRect): TableSlideAnimationState => {
  const finalTableTop = SLIDE_DOWN_HEIGHT + SLIDE_DOWN_TABLE_GAP;
  const collisionDistance = Math.max(tableRect.top - SLIDE_DOWN_TABLE_GAP, 0);
  const pushDelayMs = Math.min(
    (collisionDistance / SLIDE_DOWN_HEIGHT) * SLIDE_DOWN_ANIMATION_DURATION_MS,
    SLIDE_DOWN_ANIMATION_DURATION_MS
  );
  const pushDurationMs = Math.max(SLIDE_DOWN_ANIMATION_DURATION_MS - pushDelayMs, 0);
  const finalTableHeight = Math.max(window.innerHeight - finalTableTop - TABLE_VIEWPORT_BOTTOM_MARGIN, 0);

  return {
    fixedTop: toPx(tableRect.top),
    fixedLeft: toPx(tableRect.left),
    fixedRight: toPx(window.innerWidth - tableRect.right),
    fixedHeight: toPx(tableRect.height),
    finalHeight: toPx(finalTableHeight),
    pushDistance: toPx(finalTableTop - tableRect.top),
    pushDelay: toMs(pushDelayMs),
    pushDuration: toMs(pushDurationMs),
    resizeDelay: toMs(pushDelayMs + pushDurationMs),
  };
};

/**
 * テーブルをスライドダウンの動きに追従させるための値を更新する
 */
const prepareTableSlideAnimation = () => {
  const tableRect = tableContainerRef.value?.getBoundingClientRect();
  if (!tableRect) return;

  tableSlideAnimationState.value = createTableSlideAnimationState(tableRect);
};

/**
 * ドロップダウンを開く
 */
const openDropdown = () => {
  prepareTableSlideAnimation();
  state.value.editDropdownState = true;
};

/**
 * ドロップダウンを再表示する
 * @param targetInfo - 対象データ
 */
const reShowDropdown = (targetInfo: Partial<IBusiness>) => {
  state.value.editDropdownState = true;
  nextTick(() => {
    if (!updateSectionRefWithMode.value) return;
    updateSectionRefWithMode.value.initializeForm(targetInfo);
    updateSectionRefWithMode.value.isEdited = true;
  });
};

/**
 * 共通エラーモーダルを出す
 */
const showCommonErrorModal = () =>
  showInfoModal({
    modalTitle: t('エラーが発生しました'),
    modalDescription: t('更新中にエラーが発生しました。時間をおいて再度お試しください。'),
    isDisabledNextButton: true,
    backButtonLabel: t('OK'),
    icon: 'error',
    colorStyle: 'error',
  });

/**
 * 保存ボタン押下
 */
const onSave = async () => {
  if (!updateSectionRefWithMode.value) return;
  const commonValidateResult = updateSectionRefWithMode.value?.allValidate();

  const additionalInvalidItems = additionalValidate(updateSectionRefWithMode.value?.fieldState);
  for (const item of additionalInvalidItems) {
    const targetState = updateSectionRefWithMode.value?.fieldState[item];
    if (!targetState) continue;
    targetState.isError.value = true;
    targetState.errorMessage.value = t('入力してください');
  }
  await nextTick();
  if (!commonValidateResult || additionalInvalidItems.length) return;

  state.value.editDropdownState = false;
  const targetInfo =
    state.value.businessMode === 'baseBusiness'
      ? updateBaseBusinessWideSectionRef.value?.createBaseBusinessPatch()
      : updateAggregateBusinessWideSectionRef.value?.createAggregateBusinessPatch();
  if (!targetInfo) {
    await showCommonErrorModal();
    return;
  }
  const nullableFormState = updateSectionRefWithMode.value.nullableFormState;
  const nullKeys = Object.keys(nullableFormState).filter((k) => nullableFormState[k]);
  const useCase =
    state.value.businessMode === 'baseBusiness' ? diContainer.bulkPutBaseBusinessUseCase : diContainer.bulkPutAggregateBusinessUseCase;
  const res = await waitLoading(useCase.execute([...state.value.checkedIds], targetInfo, nullKeys));
  if (res?.status.type === 'success') {
    state.value.loadingStatus.type = 'loading';
    fetchEditBusiness();
    toast.success('更新に成功しました');
  } else {
    if (res?.status.error.code === ErrorResponseCode.ResourceConflict) {
      await showInfoModal({
        modalTitle: t('エラーが発生しました'),
        modalDescription: t('一括変更後の取引が既存の取引と重複しています。既存の取引を確認してください。'),
        isDisabledNextButton: true,
        backButtonLabel: t('OK'),
        icon: 'error',
        colorStyle: 'error',
      });
    } else {
      await showCommonErrorModal();
    }
    reShowDropdown(targetInfo);
  }
};

/**
 * ハイライトされているかどうか
 * @param businessId - 取引ID
 */
const getIsChecked = (businessId: string) => state.value.checkedIds.has(businessId);

// #endregion

// #region Event callbacks
onMounted(async () => {
  await init();
});
// #endregion
</script>
<template>
  <EditPageLayout :title="pageTitle">
    <div class="main" :class="{ 'main--slide-open': state.editDropdownState }">
      <div class="desc">
        {{ t('取引を選択して一括で項目の値を変更できます。') }}
      </div>
      <div class="header">
        <SegmentControl
          :model-value="state.businessMode"
          value-a="aggregateBusiness"
          :label-a="t('仕入取引')"
          value-b="baseBusiness"
          :label-b="t('販売取引')"
          size="sm"
          @update:model-value="changeBusinessMode"
        />
        <TextButton @click="openDropdown">
          {{ t('一括編集') }}
        </TextButton>
        <FilterModalButton filter-modal-type="inventory" :filter-items="state.filterItems" @apply="applyFilter" />
        <Pagination
          :paging-options="state.pagingOptions"
          :is-loading="state.loadingStatus.type === 'loading'"
          no-bottom-margin
          class="header__paging"
          @on-change-page="changePaging"
        />
      </div>
      <div ref="tableContainerRef" class="table-container">
        <EzTable ref="tableRef" :grid-template-columns="headerWidths" :loading-status="state.loadingStatus" class="list-table">
          <template #header>
            <template v-for="header in headerOptions" :key="`header-${header.key}`">
              <EzHeader v-if="header.key === 'checkbox'" sticky="left" class="list-table__header">
                <TableCheckbox :is-checked="isAllChecked" :is-indeterminate="isIndeterminate" @change="changeHeaderCheckbox" />
              </EzHeader>
              <EzHeader v-else-if="header.key === 'headerModify'" sticky="right" class="list-table__header">
                <TableHeaderModifyButton :modifiable-header-list="ModifiableHeaderKeys" @toggle="updateDisplayHeader" />
              </EzHeader>
              <EzHeader v-else use-resizer class="list-table__header" @resizer="(e) => onMousedownResizer(e, header.key)">
                {{ t(header.label) }}
              </EzHeader>
            </template>
          </template>
          <template #body>
            <EzTableRow v-for="(data, index1) in state.dataList" :key="`row-${index1}`" :grid-template-columns="headerWidths">
              <template v-for="header in headerOptions" :key="`row-${index1}-${header.key}`">
                <EzCell
                  v-if="header.key === 'checkbox'"
                  sticky="left"
                  :line-height="24"
                  :is-highlighted="getIsChecked(data.businessId)"
                  class="list-table__cell"
                >
                  <TableCheckbox
                    :is-checked="getIsChecked(data.businessId)"
                    @change="(isChecked) => changeCheckbox(data.businessId, isChecked)"
                  />
                </EzCell>
                <EzCell
                  v-else-if="header.key === 'headerModify'"
                  :line-height="24"
                  :is-highlighted="getIsChecked(data.businessId)"
                  class="list-table__cell"
                />
                <EzCell
                  v-else
                  :is-number="header.type === 'num'"
                  :line-height="24"
                  :is-highlighted="getIsChecked(data.businessId)"
                  class="list-table__cell"
                  :class="{
                    'list-table__cell--edited':
                      state.checkedIds.has(data.businessId) &&
                      vm.hasEditedDiff(
                        header.key,
                        data,
                        updateSectionRefWithMode?.fieldState,
                        updateSectionRefWithMode?.nullableFormState,
                        updateSectionRefWithMode?.businessFreeColumnValues
                      ),
                  }"
                >
                  {{ vm.getDisplayData(header.key, data) }}
                  <template
                    v-if="
                      state.checkedIds.has(data.businessId) &&
                      vm.hasEditedDiff(
                        header.key,
                        data,
                        updateSectionRefWithMode?.fieldState,
                        updateSectionRefWithMode?.nullableFormState,
                        updateSectionRefWithMode?.businessFreeColumnValues
                      )
                    "
                  >
                    {{
                      vm.getDisplayDiffData(
                        header.key,
                        updateSectionRefWithMode?.fieldState,
                        updateSectionRefWithMode?.nullableFormState,
                        updateSectionRefWithMode?.selectedUserAccounts,
                        updateSectionRefWithMode?.businessFreeColumnValues
                      )
                    }}
                  </template>
                </EzCell>
              </template>
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </div>
  </EditPageLayout>
  <TopSlideDownModal :is-open="state.editDropdownState" :height="SLIDE_DOWN_HEIGHT" :z-index="SLIDE_DOWN_Z">
    <div class="edit-slide-down">
      <template v-if="state.businessMode === 'baseBusiness'">
        <UpdateBaseBusinessWideSection ref="updateBaseBusinessWideSectionRef" />
      </template>
      <template v-else-if="state.businessMode === 'aggregateBusiness'">
        <UpdateAggregateBusinessWideSection ref="updateAggregateBusinessWideSectionRef" />
      </template>
      <div class="edit-slide-down__control">
        <div class="edit-slide-down__control__info">
          {{ t('選択中の取引') }}
          <span class="edit-slide-down__control__info--num">{{ state.checkedIds.size }}</span>
          {{ t('件') }}
        </div>
        <TextButton button-style="btn-secondary" @click="state.editDropdownState = false">
          {{ t('閉じる') }}
        </TextButton>
        <TextButton :disabled="!state.checkedIds.size || !updateSectionRefWithMode?.isEdited" @click="onSave">
          {{ t('更新する') }}
        </TextButton>
      </div>
    </div>
  </TopSlideDownModal>
</template>
<style lang="scss" scoped>
.desc {
  font-size: 15px;
  padding-bottom: 8px;
}

.main {
  padding-top: 8px;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 8px;
  height: calc(100dvh - 120px);
}

.header {
  display: grid;
  grid-template-columns: max-content max-content max-content minmax(16px, 1fr);
  align-items: center;
  gap: 8px;

  &__paging {
    align-self: end;
  }
}

.table-container {
  min-height: 0;
  overflow: hidden;
  background-color: $semanticWhite;
}

.list-table {
  position: relative;
  height: 100%;
  overflow-x: auto;
  background-color: $semanticWhite;
  &__header {
    height: 36px;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }
  &__row {
    &--all-select {
      color: $semanticPrimary;
      border-bottom: 1px solid $borderActive;
    }
  }
  &__cell {
    height: 24px;
    font-weight: 400;
    font-size: 12px;
    &--all-select {
      grid-column: 2 / -1;
    }
    &--edited {
      background-color: $semanticPrimarySubdued;
    }
  }
}

.main--slide-open {
  .table-container {
    position: fixed;
    top: v-bind('tableSlideAnimationState.fixedTop');
    right: v-bind('tableSlideAnimationState.fixedRight');
    left: v-bind('tableSlideAnimationState.fixedLeft');
    height: v-bind('tableSlideAnimationState.fixedHeight');
    z-index: 0;
    background-color: $semanticWhite;
    transform: translateY(0);
    will-change: transform;
    animation: tablePushDown v-bind('tableSlideAnimationState.pushDuration') ease-out v-bind('tableSlideAnimationState.pushDelay') both,
      tableResize v-bind('`${TABLE_RESIZE_ANIMATION_DURATION_MS}ms`') ease-out v-bind('tableSlideAnimationState.resizeDelay') both;
  }

  @keyframes tablePushDown {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(v-bind('tableSlideAnimationState.pushDistance'));
    }
  }

  @keyframes tableResize {
    from {
      height: v-bind('tableSlideAnimationState.fixedHeight');
    }
    to {
      height: v-bind('tableSlideAnimationState.finalHeight');
    }
  }
}

.edit-slide-down {
  height: 100%;
  padding: 0 16px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 16px;
  align-items: center;

  &__control {
    align-self: center;
    height: auto;
    padding: 12px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    grid-template-rows: auto auto auto;
    &__info {
      grid-column: auto;
      text-align: center;
      font-size: 14px;
      &--num {
        display: inline-block;
        min-width: 32px;
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
}
</style>
